import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { useAuthStore } from '../../store/authStore';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { BackButton } from '../../components/ui/BackButton';
import { eventsApi } from '../../api/events';
import { athletesApi } from '../../api/athletes';
import {
  EVENT_TYPE_LABELS,
  EVENT_TYPE_ICONS,
  PARTICIPATION_STATUS_LABELS,
  PARTICIPATION_STATUS_COLORS,
  PARTICIPANT_PAYMENT_LABELS,
  PARTICIPANT_PAYMENT_COLORS,
  formatEventDate,
  type EventType,
  type ParticipationStatus,
  type ParticipantPaymentStatus,
  type UpdateEventRequest,
} from '../../types/event';
import type { Athlete } from '../../types/api';

export function EventDetailScreen() {
  const { getCurrentScreen, popScreen } = useTabNavigationStore();
  const currentScreen = getCurrentScreen();
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId);
  const queryClient = useQueryClient();

  const eventId = currentScreen.params?.eventId as string;

  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [editForm, setEditForm] = useState<UpdateEventRequest>({});
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');

  const { data: event, isLoading } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => eventsApi.getById(eventId),
    enabled: !!eventId,
  });

  const { data: participants } = useQuery({
    queryKey: ['event-participants', eventId],
    queryFn: () => eventsApi.getParticipants(eventId),
    enabled: !!eventId,
  });

  const { data: allAthletes } = useQuery({
    queryKey: ['athletes', currentAssistantId],
    queryFn: () => athletesApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId && showAddParticipant,
  });

  const participantIds = new Set((participants || []).map((p) => p.athlete_id));
  const availableAthletes = (allAthletes || []).filter((a: Athlete) => !participantIds.has(a.id));

  const updateMutation = useMutation({
    mutationFn: (data: UpdateEventRequest) => eventsApi.update(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      queryClient.invalidateQueries({ queryKey: ['events', currentAssistantId] });
      setIsEditing(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => eventsApi.delete(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', currentAssistantId] });
      popScreen();
    },
  });

  const addParticipantMutation = useMutation({
    mutationFn: (athleteId: string) => eventsApi.addParticipant(eventId, { athlete_id: athleteId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['event-participants', eventId] }),
  });

  const bulkAddMutation = useMutation({
    mutationFn: (athleteIds: string[]) => eventsApi.bulkAddParticipants(eventId, { athlete_ids: athleteIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-participants', eventId] });
      setShowAddParticipant(false);
    },
  });

  const updateParticipantMutation = useMutation({
    mutationFn: ({ athleteId, data }: { athleteId: string; data: { status?: ParticipationStatus; payment_status?: ParticipantPaymentStatus } }) =>
      eventsApi.updateParticipant(eventId, athleteId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['event-participants', eventId] }),
  });

  const removeParticipantMutation = useMutation({
    mutationFn: (athleteId: string) => eventsApi.removeParticipant(eventId, athleteId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['event-participants', eventId] }),
  });

  const extractTime = (isoDate: string): string => {
    if (!isoDate || !isoDate.includes('T')) return '';
    const timePart = isoDate.split('T')[1];
    if (!timePart) return '';
    const hhmm = timePart.substring(0, 5);
    return hhmm === '00:00' ? '' : hhmm;
  };

  const combineDateAndTime = (date: string, time: string): string => {
    if (!date) return '';
    if (!time) return date;
    // Add timezone offset for RFC3339 format
    const offset = new Date().getTimezoneOffset();
    const sign = offset <= 0 ? '+' : '-';
    const absOffset = Math.abs(offset);
    const hours = String(Math.floor(absOffset / 60)).padStart(2, '0');
    const minutes = String(absOffset % 60).padStart(2, '0');
    return `${date}T${time}:00${sign}${hours}:${minutes}`;
  };

  const handleStartEdit = () => {
    if (!event) return;
    setEditForm({
      name: event.name,
      event_type: event.event_type,
      description: event.description || '',
      location: event.location || '',
      start_date: event.start_date.split('T')[0],
      end_date: event.end_date ? event.end_date.split('T')[0] : '',
      payment_amount: event.payment_amount || '',
    });
    setEditStartTime(extractTime(event.start_date));
    setEditEndTime(event.end_date ? extractTime(event.end_date) : '');
    setIsEditing(true);
  };

  const handleSave = () => {
    updateMutation.mutate({
      ...editForm,
      start_date: editForm.start_date ? combineDateAndTime(editForm.start_date, editStartTime) : undefined,
      description: editForm.description || undefined,
      location: editForm.location || undefined,
      end_date: editForm.end_date ? combineDateAndTime(editForm.end_date, editEndTime) : undefined,
      payment_amount: editForm.payment_amount || undefined,
    });
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', borderRadius: '10px',
    border: '1px solid var(--separator)', backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)', fontSize: '17px', outline: 'none',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: '13px', color: 'var(--text-hint)', marginBottom: '4px',
  };

  if (!eventId) return <div className="p-4" style={{ color: 'var(--danger)' }}>Событие не найдено</div>;

  if (isLoading) {
    return <div className="flex items-center justify-center h-full"><p style={{ color: 'var(--text-hint)' }}>Загрузка...</p></div>;
  }

  if (!event) {
    return <div className="p-4"><Card><p className="text-center py-8" style={{ color: 'var(--text-hint)' }}>Событие не найдено</p></Card></div>;
  }

  return (
    <div className="p-4 space-y-4 pb-24">
      <BackButton />

      {/* Event info */}
      <Card>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl">{EVENT_TYPE_ICONS[event.event_type]}</span>
          <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{isEditing ? 'Редактирование' : event.name}</h2>
        </div>

        {isEditing ? (
          <div className="space-y-3">
            <div><label style={labelStyle}>Название</label><input type="text" value={editForm.name || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))} style={inputStyle} /></div>
            <div>
              <label style={labelStyle}>Тип</label>
              <select value={editForm.event_type || event.event_type} onChange={(e) => setEditForm((prev) => ({ ...prev, event_type: e.target.value as EventType }))} style={{ ...inputStyle, appearance: 'none', cursor: 'pointer' }}>
                {Object.entries(EVENT_TYPE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <div className="flex gap-2">
                <div className="flex-1"><label style={labelStyle}>Начало</label><input type="date" value={editForm.start_date || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, start_date: e.target.value }))} style={inputStyle} /></div>
                <div style={{ width: '120px' }}><label style={labelStyle}>Время</label><input type="time" value={editStartTime} onChange={(e) => setEditStartTime(e.target.value)} style={inputStyle} /></div>
              </div>
              <div className="flex gap-2">
                <div className="flex-1"><label style={labelStyle}>Окончание</label><input type="date" value={editForm.end_date || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, end_date: e.target.value }))} style={inputStyle} /></div>
                <div style={{ width: '120px' }}><label style={labelStyle}>Время</label><input type="time" value={editEndTime} onChange={(e) => setEditEndTime(e.target.value)} style={inputStyle} /></div>
              </div>
            </div>
            <div><label style={labelStyle}>Место</label><input type="text" value={editForm.location || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, location: e.target.value }))} style={inputStyle} /></div>
            <div><label style={labelStyle}>Стоимость</label><input type="number" value={editForm.payment_amount || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, payment_amount: e.target.value }))} style={inputStyle} /></div>
            <div><label style={labelStyle}>Описание</label><textarea value={editForm.description || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'none' }} /></div>
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={updateMutation.isPending} fullWidth>{updateMutation.isPending ? 'Сохранение...' : 'Сохранить'}</Button>
              <Button variant="secondary" onClick={() => setIsEditing(false)}>Отмена</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--accent)' }}>
              {EVENT_TYPE_LABELS[event.event_type]}
            </span>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm" style={{ color: 'var(--text-hint)' }}>Дата:</span>
                <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  {formatEventDate(event.start_date)}
                  {event.end_date && ` -- ${formatEventDate(event.end_date)}`}
                </span>
              </div>
              {event.location && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: 'var(--text-hint)' }}>Место:</span>
                  <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{event.location}</span>
                </div>
              )}
              {event.payment_amount && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: 'var(--text-hint)' }}>Стоимость:</span>
                  <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    {parseFloat(event.payment_amount).toLocaleString('ru-RU')} rub
                  </span>
                </div>
              )}
              {event.description && (
                <div>
                  <span className="text-sm" style={{ color: 'var(--text-hint)' }}>Описание:</span>
                  <p className="text-sm mt-1 whitespace-pre-wrap" style={{ color: 'var(--text-primary)' }}>{event.description}</p>
                </div>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleStartEdit} fullWidth>Редактировать</Button>
              <Button variant="danger" onClick={() => setShowDeleteConfirm(true)} disabled={deleteMutation.isPending}>Удалить</Button>
            </div>
          </div>
        )}
      </Card>

      {/* Participants */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[17px] font-semibold" style={{ color: 'var(--text-primary)' }}>Участники ({participants?.length || 0})</h3>
          <Button onClick={() => setShowAddParticipant(!showAddParticipant)}>
            {showAddParticipant ? 'Скрыть' : '+ Добавить'}
          </Button>
        </div>

        {showAddParticipant && (
          <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--separator)' }}>
            <p className="text-sm mb-2" style={{ color: 'var(--text-hint)' }}>Выберите спортсменов:</p>
            {availableAthletes.length > 0 ? (
              <>
                <div className="space-y-1 max-h-48 overflow-y-auto mb-2">
                  {availableAthletes.map((athlete: Athlete) => (
                    <button key={athlete.id} onClick={() => addParticipantMutation.mutate(athlete.id)} disabled={addParticipantMutation.isPending} className="w-full text-left px-3 py-2 rounded text-sm active:opacity-60 transition-opacity" style={{ color: 'var(--text-primary)' }}>
                      {athlete.name}
                    </button>
                  ))}
                </div>
                <Button variant="secondary" onClick={() => bulkAddMutation.mutate(availableAthletes.map((a: Athlete) => a.id))} disabled={bulkAddMutation.isPending} fullWidth>
                  {bulkAddMutation.isPending ? 'Добавление...' : `Добавить всех (${availableAthletes.length})`}
                </Button>
              </>
            ) : (
              <p className="text-sm italic" style={{ color: 'var(--text-hint)' }}>Все спортсмены уже добавлены</p>
            )}
          </div>
        )}

        {participants && participants.length > 0 ? (
          <div className="space-y-2">
            {participants.map((p) => (
              <div key={p.id} className="flex items-center justify-between p-2 rounded-lg" style={{ border: '1px solid var(--separator)' }}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{p.athlete_name || 'Спортсмен'}</p>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    <select
                      value={p.status}
                      onChange={(e) => updateParticipantMutation.mutate({ athleteId: p.athlete_id, data: { status: e.target.value as ParticipationStatus } })}
                      className={`text-xs px-2 py-0.5 rounded-full border-0 ${PARTICIPATION_STATUS_COLORS[p.status]}`}
                    >
                      {Object.entries(PARTICIPATION_STATUS_LABELS).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                    {event.payment_amount && (
                      <select
                        value={p.payment_status}
                        onChange={(e) => updateParticipantMutation.mutate({ athleteId: p.athlete_id, data: { payment_status: e.target.value as ParticipantPaymentStatus } })}
                        className={`text-xs px-2 py-0.5 rounded-full border-0 ${PARTICIPANT_PAYMENT_COLORS[p.payment_status]}`}
                      >
                        {Object.entries(PARTICIPANT_PAYMENT_LABELS).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
                <button onClick={() => removeParticipantMutation.mutate(p.athlete_id)} className="text-xs px-2 ml-2 flex-shrink-0" style={{ color: 'var(--danger)' }}>
                  X
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center py-4" style={{ color: 'var(--text-hint)' }}>Нет участников</p>
        )}
      </Card>

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center" onClick={() => setShowDeleteConfirm(false)}>
          <div className="mx-4 rounded-xl p-6 max-w-sm w-full" style={{ backgroundColor: 'var(--bg-primary)' }} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[17px] font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Удалить событие?</h3>
            <p className="text-sm mb-4" style={{ color: 'var(--text-hint)' }}>Событие и все данные об участниках будут удалены.</p>
            <div className="flex gap-2">
              <Button variant="danger" onClick={() => deleteMutation.mutate()} fullWidth>Удалить</Button>
              <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)} fullWidth>Отмена</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
